import {
  Button,
  ChildrenIcon,
  CloseIcon,
  ConsentOrdersChildrenSchema,
  DatePicker,
  InitialDetails,
  RadioSelect,
  Select,
  TextInput,
  useNames,
  useOptions
} from "@fla/components/src";
import { yupResolver } from "@hookform/resolvers/yup";
import classNames from "classnames";
import "definitions/Yup/yup-extended";
import { selectConsentOrdersBase } from "pages/ConsentOrders";
import { useFieldArray, useForm } from "react-hook-form";
import { useSelector } from "react-redux";

interface IProps {
  formId: string;
  store: InitialDetails;
  onSubmit: (values: InitialDetails) => void;
}

export default function Children({ onSubmit, formId, store }: IProps) {
  const { handleSubmit, control, watch, reset } = useForm({
    mode: "all",
    defaultValues: store,
    resolver: yupResolver(ConsentOrdersChildrenSchema)
  });

  const { fields, append, remove } = useFieldArray({
    control, // control props comes from useForm (optional: if you are using FormContext)
    name: "children.child" // unique name for your Field Array
  });
  const { initialDetails } = useSelector(selectConsentOrdersBase);

  const { primaryCareGiverList } = useOptions({ initialDetails });
  const { spouseFirstName } = useNames({ initialDetails });

  const appendChild = () => {
    append({
      first_name: "",
      last_name: "",
      gender: "",
      dob: "",
      caregiver: "",
      other_caregiver: {
        first_name: "",
        last_name: ""
      }
    });
  };

  const removeChildDetails = (idx: number) => {
    if (watch("children.child")?.length === 1 && idx === 0) {
      reset();
    } else {
      remove(idx);
    }
  };

  const submit = (values: any) => {
    if (values?.children?.if_children === "no") {
      onSubmit({
        ...values,
        children: {
          ...values.children,
          if_live_with_anyone: "",
          live_with: [],
          if_live_with_anyone_at_spouse: "",
          live_with_at_spouse: ""
        }
      });
    } else onSubmit(values);
  };

  return (
    <form id={formId} className="space-y-5" onSubmit={handleSubmit(submit)}>
      <div className="mb-8 pb-8 border-b">
        <ChildrenIcon classes="w-14 h-14 mx-auto mb-5" />
        <h3 className="text-center">Children</h3>
      </div>
      <RadioSelect
        title={`Are either you or ${spouseFirstName} the parent of a child under 18 years old?`}
        subTitle="Include only biological or adopted children. Include any children from a previous relationship as well."
        control={control}
        name="children.if_children"
        id="children.if_children"
        options={["yes", "no"]}
        onChange={val => {
          if (val === "yes") {
            appendChild();
          }
          if (val === "no") {
            remove();
          }
        }}
      />

      <div className="space-y-3">
        {fields.map(({ id }, idx) => (
          <section key={id} className="bg-[#f2f2f2] p-5 rounded-[10px] space-y-5">
            <div
              className={classNames("flex items-center pb-5 border-b ", {
                "justify-between": idx !== 0,
                "justify-start": idx === 0
              })}
            >
              <div className="w-full font-bold">Child {idx + 1}</div>
              {idx > 0 ? (
                <button
                  type="button"
                  className="transition-all hover:bg-red-400/20 p-2 rounded-lg"
                  onClick={() => removeChildDetails(idx)}
                >
                  <CloseIcon strokeColor="text-red-500" title="remove child details" />
                </button>
              ) : null}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <TextInput
                title="First Name"
                control={control}
                name={`children.child.${idx}.first_name`}
                id={`children.child.${idx}.first_name`}
                placeholder="First Name"
                theme="dark"
              />
              <TextInput
                title="Last Name"
                control={control}
                name={`children.child.${idx}.last_name`}
                id={`children.child.${idx}.last_name`}
                placeholder="Last Name"
                theme="dark"
              />
            </div>

            <RadioSelect
              title="Gender"
              control={control}
              name={`children.child.${idx}.gender`}
              id={`children.child.${idx}.gender`}
              options={["male", "female"]}
              theme="dark"
            />

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <DatePicker
                title="Date Of Birth"
                control={control}
                name={`children.child.${idx}.dob`}
                version="v2"
                theme="dark"
                // id={`children.child.${idx}.dob`}
                // classes="col-span-1"
                // theme="dark"
              />

              <Select
                title="Who is the primary caregiver"
                control={control}
                name={`children.child.${idx}.caregiver`}
                id={`children.child.${idx}.caregiver`}
                placeholder="Please Select"
                options={primaryCareGiverList}
                theme="dark"
              />
            </div>

            {watch(`children.child.${idx}.caregiver`) === "other" ? (
              <article className="grid grid-cols-2 gap-x-4">
                <p className="font-bold text-sm col-span-2 mb-2">Name Of The Other Caregiver</p>
                <TextInput
                  title={null}
                  control={control}
                  name={`children.child.${idx}.other_caregiver.first_name`}
                  id={`children.child.${idx}.other_caregiver.first_name`}
                  placeholder="First Name"
                  theme="dark"
                />
                <TextInput
                  title={null}
                  control={control}
                  name={`children.child.${idx}.other_caregiver.last_name`}
                  id={`children.child.${idx}.other_caregiver.last_name`}
                  placeholder="Last Name"
                  theme="dark"
                />
              </article>
            ) : null}
          </section>
        ))}
      </div>

      {/*
      other_resident_user - Yes/No - 

      Does anyone else live in this child's home when the child is living with you ?
            Subtext "do not include any other children you're adding here"

----
if yes 
label: other_resident_details_user 
text-area
title: Give the full name(s), relationship to the child, age of each other person who lives with the child.
subText "Don't include yourself, {exFirstname} or any other child you're adding here "

----

other_resident_spouse
yes / no

Does anyone else live in this child's home when the child is living with {exFirstName)} ?
  Subtext "do not include any other children you're adding here"

-----
if yes 
label: other_resident_details_spouse 
text-area
title: Give the full name(s), relationship to the child, age of each other person who lives with the child.
subText "Don't include yourself, {exFirstname} or any other child you're adding here "

      */}

      {watch("children.if_children") === "yes" ? (
        <Button
          className="flex items-center justify-center px-7 py-2 mx-auto my-20 uppercase font-semibold"
          onClick={appendChild}
          type="button"
          // appearance="outline"
        >
          &#43; Add Another Child
        </Button>
      ) : null}
    </form>
  );
}
